import { useMemo, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import {
  AlertTriangle,
  CheckCircle2,
  Clock,
  Loader2,
  Plus,
  RefreshCw,
  ShieldCheck,
  XCircle,
} from 'lucide-react'
import clsx from 'clsx'
import { Button } from '@/components/common/Button'
import { EmptyState } from '@/components/common/EmptyState'
import { LoadingSpinner } from '@/components/common/LoadingSpinner'
import { DevActorSwitch } from '@/components/patching/DevActorSwitch'
import { StageStepper } from '@/components/patching/StageStepper'
import { usePatchCampaigns, usePatchMe } from '@/hooks/usePatching'
import { formatDateTime } from '@/utils/format'
import { campaignBucket, campaignHeadline, hasRunningStage, type CampaignBucket } from '@/utils/patchStage'
import type { PatchCampaign } from '@/types/patching'
import styles from './PatchManagementPage.module.css'

const TABS: { id: CampaignBucket; label: string; hint?: string }[] = [
  { id: 'active', label: 'Active' },
  { id: 'awaiting', label: 'Awaiting approval' },
  { id: 'completed', label: 'Completed' },
  { id: 'failed', label: 'Failed', hint: 'Includes campaigns with a failed, rejected or cancelled environment' },
]

const BUCKET_CLASS: Record<CampaignBucket, string> = {
  active: styles.bucketActive,
  awaiting: styles.bucketAwaiting,
  completed: styles.bucketCompleted,
  failed: styles.bucketFailed,
}

function CampaignRow({ campaign }: { campaign: PatchCampaign }) {
  const bucket = campaignBucket(campaign)
  return (
    <tr className={styles.tableRow} data-testid="campaign-row" data-campaign={campaign.id} data-bucket={bucket}>
      <td className={styles.cellId}>
        <Link to={`/patch-management/${campaign.id}`} className={styles.idLink}>
          {campaign.id}
        </Link>
      </td>
      <td>
        <div className={styles.componentName}>{campaign.name}</div>
        {campaign.change_ticket && <div className={styles.componentDesc}>Change {campaign.change_ticket}</div>}
      </td>
      <td>
        <span className={styles.envBadge}>{campaign.product || '—'}</span>
      </td>
      <td>
        <span className={clsx(styles.statusBadge, BUCKET_CLASS[bucket])} data-testid="campaign-status">
          {campaignHeadline(campaign)}
        </span>
      </td>
      <td>
        <StageStepper campaign={campaign} size="mini" />
      </td>
      <td className={styles.cellMuted}>{campaign.requested_by_name || campaign.requested_by || '—'}</td>
      <td className={styles.cellMuted}>{campaign.created_at ? formatDateTime(campaign.created_at) : '—'}</td>
    </tr>
  )
}

export function PatchManagementPage() {
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<CampaignBucket>('active')
  const { data, isLoading, isError, error, refetch, isFetching } = usePatchCampaigns()
  const { data: me } = usePatchMe()

  const campaigns = useMemo(() => data?.campaigns ?? [], [data])
  const buckets = useMemo(() => {
    const grouped: Record<CampaignBucket, PatchCampaign[]> = { active: [], awaiting: [], completed: [], failed: [] }
    for (const campaign of campaigns) grouped[campaignBucket(campaign)].push(campaign)
    return grouped
  }, [campaigns])
  const running = campaigns.filter(hasRunningStage).length
  // Shown only when the portal has told us this user may request. While /me is loading or if it failed, we do not guess.
  const canCreate = me?.can_request === true
  const displayed = buckets[activeTab]

  const newCampaign = (
    <Button icon={<Plus size={14} />} onClick={() => navigate('/patch-management/new')}>
      New campaign
    </Button>
  )

  return (
    <div className={styles.page}>
      {/* Header */}
      <div className={styles.pageHeader}>
        <div className={styles.headerLeft}>
          <div className={styles.headerIcon}>
            <ShieldCheck size={20} />
          </div>
          <div>
            <h1 className={styles.pageTitle}>Patch Management</h1>
            <p className={styles.pageSubtitle}>Plan, approve and roll out OS patch campaigns DEV → UAT → PROD, one environment at a time</p>
          </div>
        </div>
        <div className={styles.headerActions}>
          <button className={styles.btnSecondary} onClick={() => refetch()} disabled={isFetching} aria-label="Refresh campaigns">
            <RefreshCw size={14} className={isFetching ? styles.spin : undefined} />
            Refresh
          </button>
          {canCreate && (
            <Button variant="secondary" onClick={() => navigate('/patch-management/azure')}>
              Azure inventory
            </Button>
          )}
          {canCreate && newCampaign}
          <DevActorSwitch />
        </div>
      </div>

      {isLoading && <LoadingSpinner label="Loading campaigns…" />}

      {isError && (
        <div className={styles.errorBox} role="alert" data-testid="campaigns-error">
          <AlertTriangle size={16} />
          <span>Could not load patch campaigns: {error.message}</span>
          <button className={styles.btnSecondary} onClick={() => refetch()}>Retry</button>
        </div>
      )}

      {!isLoading && !isError && campaigns.length === 0 && (
        <div className={styles.tableCard}>
          <EmptyState
            icon={<ShieldCheck size={32} />}
            title="No patch campaigns yet"
            description="Create a campaign to plan and roll out patches to your VMs."
            action={canCreate ? newCampaign : undefined}
          />
        </div>
      )}

      {campaigns.length > 0 && (
        <>
          {/* Stats — derived from the campaigns returned by the portal */}
          <div className={styles.statsRow} data-testid="stats-row">
            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'var(--aptean-info-bg)', color: 'var(--aptean-info)' }}>
                <ShieldCheck size={18} />
              </div>
              <div>
                <div className={styles.statValue} data-testid="stat-total">{campaigns.length}</div>
                <div className={styles.statLabel}>Campaigns</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'var(--aptean-teal-subtle)', color: 'var(--aptean-teal-dark)' }}>
                <Loader2 size={18} />
              </div>
              <div>
                <div className={styles.statValue} data-testid="stat-running">{running}</div>
                <div className={styles.statLabel}>Running now</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div className={styles.statIcon} style={{ background: 'var(--aptean-warning-bg)', color: 'var(--aptean-warning)' }}>
                <Clock size={18} />
              </div>
              <div>
                <div className={styles.statValue} data-testid="stat-awaiting">{buckets.awaiting.length}</div>
                <div className={styles.statLabel}>Awaiting approval</div>
              </div>
            </div>
            <div className={styles.statCard}>
              <div
                className={styles.statIcon}
                style={{
                  background: buckets.failed.length > 0 ? 'var(--aptean-error-bg)' : 'var(--aptean-success-bg)',
                  color: buckets.failed.length > 0 ? 'var(--aptean-error)' : 'var(--aptean-success)',
                }}
              >
                {buckets.failed.length > 0 ? <XCircle size={18} /> : <CheckCircle2 size={18} />}
              </div>
              <div>
                <div className={styles.statValue} data-testid="stat-failed">{buckets.failed.length}</div>
                <div className={styles.statLabel}>Failed / stopped</div>
              </div>
            </div>
          </div>

          {data && data.count > campaigns.length && (
            <p className={styles.partialNote} data-testid="partial-note">
              Showing the first {campaigns.length} of {data.count} campaigns; the counts above cover only these.
            </p>
          )}

          {/* Tabs */}
          <div className={styles.tabs} role="tablist" aria-label="Campaign status">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={activeTab === tab.id}
                title={tab.hint}
                className={clsx(styles.tab, activeTab === tab.id && styles.tabActive)}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                <span className={clsx(styles.tabBadge, activeTab === tab.id && styles.tabBadgeActive)}>
                  {buckets[tab.id].length}
                </span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className={styles.tableCard}>
            {displayed.length === 0 ? (
              <p className={styles.emptyTab} data-testid="empty-tab">
                No {TABS.find((t) => t.id === activeTab)?.label.toLowerCase()} campaigns.
              </p>
            ) : (
              <div className={styles.tableWrapper}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Name</th>
                      <th>Product</th>
                      <th>Status</th>
                      <th>Environments</th>
                      <th>Requested by</th>
                      <th>Created</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayed.map((campaign) => (
                      <CampaignRow key={campaign.id} campaign={campaign} />
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  )
}
